import { authApi } from '@/api/auth';
import { setRefreshHandler } from '@/api/client';
import { meApi } from '@/api/me';
import { decodeExp } from '@/lib/jwt';
import { requireReauth } from '@/lib/reauth-gate';
import { useAuthStore } from '@/stores/auth';

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<boolean> | null = null;
let transientFailures = 0;

// Retry cadence for a refresh that failed for a reason that is not an auth
// verdict — offline laptop, slept machine, flaky network.
const RETRY_BASE_MS = 30_000;
const RETRY_MAX_MS = 5 * 60_000;

function scheduleNext(jwt: string) {
  if (refreshTimer) clearTimeout(refreshTimer);
  const exp = decodeExp(jwt);
  if (exp === null) return;
  const fireAt = (exp - 300) * 1000;
  const delay = Math.max(30_000, fireAt - Date.now());
  refreshTimer = setTimeout(() => void performRefresh(), delay);
}

/**
 * A transiently failed refresh must not leave the timer chain dead: the access
 * token would then expire unnoticed, and the next click has to renew it inline
 * (the path that used to deadlock — see isAuthEndpointUrl in api/client). Back
 * off, capped, so an offline machine does not hammer /auth/refresh.
 */
function scheduleRetry() {
  if (refreshTimer) clearTimeout(refreshTimer);
  const delay = Math.min(RETRY_BASE_MS * 2 ** transientFailures, RETRY_MAX_MS);
  transientFailures += 1;
  refreshTimer = setTimeout(() => void performRefresh(), delay);
}

function isDefinitiveAuthFailure(e: unknown): boolean {
  const status = (e as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
}

export function performRefresh(): Promise<boolean> {
  if (inFlight) return inFlight;
  inFlight = doRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doRefresh(): Promise<boolean> {
  try {
    const data = await authApi.refresh();
    if (!data.access) {
      requireReauth();
      return false;
    }
    transientFailures = 0;
    useAuthStore.getState().setAccess(data.access);
    return true;
  } catch (e) {
    if (isDefinitiveAuthFailure(e)) {
      requireReauth();
      return false;
    }
    scheduleRetry();
    return false; // 瞬时错误：不登出、不弹框
  }
}

export async function bootstrap(): Promise<void> {
  const ok = await performRefresh();
  if (ok) {
    try {
      const me = await meApi.getMe();
      useAuthStore.getState().setUser(me.user);
    } catch {
      // Tolerable — pages call useMe themselves.
    }
  }
  useAuthStore.getState().setBootstrapped();
}

setRefreshHandler(performRefresh);

// Arm the timer from every path that mints a token — bootstrap, login, reauth —
// not just from this module's own refresh. LoginPage and ReauthModal write the
// access token straight into the store, so without this a session that never
// reloads the page would never schedule a proactive refresh at all.
useAuthStore.subscribe((state, prev) => {
  if (state.access && state.access !== prev.access) scheduleNext(state.access);
});
